import type { FC } from 'react';
import React, { useState } from 'react';

import styled from '@emotion/styled';

import { COLORS, SERIF_FONT_FAMILY, mq } from '@clutter/clean';

import type { Location__Topic, PageQuery } from '@graphql/platform';

import { PageContent, PageExternalContent, PageSectionTitle } from './shared';

const PageTopicsContent = styled(PageContent)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${mq({
    margin: ['48px auto', '72px auto'],
  })}
`;

const PageTopicEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PageTopicNav = styled.div`
  display: flex;
  justify-content: space-between;
  ${mq({
    gap: ['18px', '24px'],
  })}
`;

const PageTopicToggle = styled.button`
  cursor: pointer;
  border: none;
  outline: none;
  width: 36px;
  height: 36px;
  border-radius: 128px;
  display: inline-flex;
  font-size: 24px;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;

  &[aria-expanded='true'] {
    color: ${COLORS.panther};
    background: #d9d9d9;
  }

  &[aria-expanded='false'] {
    color: ${COLORS.cloud};
    background: ${COLORS.tealJungle};
  }
`;

const PageTopicBlurb = styled.div`
  &[aria-hidden='true'] {
    display: none;
  }

  &[aria-hidden='false'] {
    display: block;
  }
`;

const PageTopicTitle = styled.h2`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  flex-grow: 1;
  flex-shrink: 1;
  ${mq({
    fontSize: ['20px', '28px'],
    lineHeight: ['140%', '40px'],
  })}
`;

const PageTopic: FC<{
  topic: Pick<Location__Topic, 'id' | 'name' | 'blurb'>;
}> = ({ topic }) => {
  const [open, setOpen] = useState(false);

  const onToggle = () => {
    setOpen((prev) => !prev);
  };

  return (
    <PageTopicEntry>
      <PageTopicNav>
        <PageTopicTitle>{topic.name}</PageTopicTitle>
        <PageTopicToggle
          aria-expanded={open}
          aria-label={open ? 'Hide' : 'Show'}
          onClick={onToggle}
        >
          <span>{open ? <>−</> : <>+</>}</span>
        </PageTopicToggle>
      </PageTopicNav>
      <PageTopicBlurb aria-hidden={!open}>
        <PageExternalContent
          dangerouslySetInnerHTML={{ __html: topic.blurb }}
        />
      </PageTopicBlurb>
    </PageTopicEntry>
  );
};

export const PageTopics: FC<{
  page: PageQuery['page'];
}> = ({ page }) => {
  if (!page) throw new Error('missing page');

  const title = page.topicsTitle.trim();

  return (
    <PageTopicsContent>
      <PageSectionTitle>{title}</PageSectionTitle>
      {page.topics.map((topic) => (
        <PageTopic key={topic.id} topic={topic} />
      ))}
    </PageTopicsContent>
  );
};
